import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GraduationCap, FileText, User, TrendingUp, Sparkles, X, Heart, Star, Target, Rocket } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import TaskWidget from '@/components/TaskWidget';

interface DashboardStats {
  totalCourses?: number;
  completedCourses?: number;
  averageGrade?: number;
  earnedCredits?: number;
}

interface AlumnoProfile {
  sueno?: string;
}

export default function AlumnoDashboard() {
  const [stats, setStats] = useState<DashboardStats>({});
  const [profile, setProfile] = useState<AlumnoProfile>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDreamModal, setShowDreamModal] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
    fetchProfile();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch('/api/grades/kardex', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar estadísticas');
      }

      const data = await response.json();
      setStats(data.statistics || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch('/api/alumnos/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.alumno || {});
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const statCards = [
    {
      title: 'Materias Totales',
      value: stats.totalCourses || 0,
      icon: GraduationCap,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Materias Completadas',
      value: stats.completedCourses || 0,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Promedio General',
      value: stats.averageGrade ? `${stats.averageGrade}` : 'N/A',
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Créditos Obtenidos',
      value: stats.earnedCredits || 0,
      icon: User,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Bienvenido a tu portal de alumno</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`w-4 h-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Resumen Académico</CardTitle>
          <CardDescription>
            Tu progreso académico actual
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Porcentaje de Avance</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {stats.totalCourses ? 
                    `${Math.round(((stats.completedCourses || 0) / stats.totalCourses) * 100)}%` : 
                    '0%'
                  }
                </span>
                {profile.sueno && (
                  <button
                    onClick={() => setShowDreamModal(true)}
                    className="p-1 rounded-full hover:bg-yellow-50 transition-colors"
                    title="Ver tu sueño"
                  >
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                  </button>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: stats.totalCourses ? 
                    `${((stats.completedCourses || 0) / stats.totalCourses) * 100}%` : 
                    '0%' 
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <a href="/alumno/kardex" className="block p-3 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center">
                <GraduationCap className="w-5 h-5 mr-3 text-blue-600" />
                <div>
                  <p className="font-medium">Ver Kardex</p>
                  <p className="text-sm text-gray-600">Consulta tus calificaciones</p>
                </div>
              </div>
            </a>
            <a href="/alumno/perfil" className="block p-3 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-3 text-green-600" />
                <div>
                  <p className="font-medium">Mi Perfil</p>
                  <p className="text-sm text-gray-600">Actualiza tu información</p>
                </div>
              </div>
            </a>
            <a href="/alumno/documentos" className="block p-3 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center">
                <FileText className="w-5 h-5 mr-3 text-purple-600" />
                <div>
                  <p className="font-medium">Documentos</p>
                  <p className="text-sm text-gray-600">Sube tus archivos</p>
                </div>
              </div>
            </a>
          </CardContent>
        </Card>

        <TaskWidget showCompleted={false} maxItems={5} />
      </div>

      {/* Dream Modal */}
      {showDreamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDreamModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-500 p-8 text-white">
              <button
                onClick={() => setShowDreamModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center justify-center mb-4">
                <div className="p-4 bg-white bg-opacity-20 rounded-full">
                  <Sparkles className="w-12 h-12" />
                </div>
              </div>
              
              <h2 className="text-3xl font-bold text-center mb-2">Tu Sueño</h2>
              <p className="text-center text-yellow-50 text-sm">El motivo que te impulsa cada día</p>
            </div>
            
            <div className="p-8">
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-6">
                <p className="text-gray-800 text-lg text-center leading-relaxed italic">
                  "{profile.sueno || 'No has compartido tu sueño aún'}"
                </p>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="flex flex-col items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <Target className="w-8 h-8 text-blue-600" />
                  <span className="text-xs text-center text-gray-700 font-medium">Enfócate</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 bg-red-50 rounded-lg">
                  <Heart className="w-8 h-8 text-red-500" />
                  <span className="text-xs text-center text-gray-700 font-medium">Ama lo que haces</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 bg-yellow-50 rounded-lg">
                  <Star className="w-8 h-8 text-yellow-500" />
                  <span className="text-xs text-center text-gray-700 font-medium">Brilla</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <Rocket className="w-8 h-8 text-purple-600" />
                  <span className="text-xs text-center text-gray-700 font-medium">Despega</span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Cada esfuerzo te acerca a tu meta. ¡Sigue adelante!
                </p>
                <Button 
                  onClick={() => setShowDreamModal(false)}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                >
                  ¡Estoy listo para seguir!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
